import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { formatCurrency } from '../../utils/formatters';
import { API_URL } from '../../utils/config';

interface BillLineItem {
  id: string;
  lineNumber: number;
  description: string;
  amount: number;
  quantity?: number;
  unitPrice?: number;
  itemName?: string;
  accountName?: string;
  taxAmount?: number;
}

interface RelatedInvoiceSummary {
  id: string;
  invoiceNumber?: string;
  customer?: string;
  totalAmount?: number;
  balanceDue?: number;
  dueDate?: string;
  mappingReason?: string;
}

interface BillDetail {
  id: string;
  transactionNumber: string;
  transactionDate: string;
  dueDate: string;
  vendorId: string;
  vendorName: string;
  totalAmount: number;
  balanceDue: number;
  status: string;
  currencyCode: string;
  memo?: string;
  privateNote?: string;
  createdTime: string;
  lastUpdatedTime: string;
  apAccountName?: string;
  vendorAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  lineItems?: BillLineItem[];
  relatedInvoice?: RelatedInvoiceSummary | null;
}

export const BillDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [bill, setBill] = useState<BillDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use refs to track fetch state - Pattern 1 from react-patterns.md
  const hasFetchedRef = useRef(false);
  const lastFetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) return;
    
    // Check if ID changed
    if (lastFetchedIdRef.current !== id) {
      lastFetchedIdRef.current = id;
      hasFetchedRef.current = false;
    }
    
    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const fetchBillDetail = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await authenticatedFetch(`${API_URL}/api/accounting/user/bills/${id}`);
        if (response && response.ok) {
          const data = await response.json();
          if (data.success) {
            setBill(data.data);
          }
        } else {
          setError('Failed to load bill details');
        }
      } catch (err) {
        setError('Failed to load bill details. Please try again later.');
        console.error('Error fetching bill detail:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchBillDetail();
  }, [id, authenticatedFetch]);

  const getStatusColor = (status?: string) => {
    const normalized = typeof status === 'string' ? status.toLowerCase() : '';
    switch (normalized) {
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'open':
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const isOverdue = (dueDate?: string, balanceDue?: number) => {
    if (!dueDate || typeof balanceDue !== 'number') return false;
    return new Date(dueDate) < new Date() && balanceDue > 0;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    // This would typically trigger a PDF download from the API
    console.log('Download bill PDF');
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !bill) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Bill not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/accounting/bills')}
          sx={{ mt: 2 }}
        >
          Back to Bills
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/accounting/vendor')}
        >
          Subsidiary Accounting
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/accounting/bills')}
        >
          Bills
        </Link>
        <Typography color="text.primary">{bill.transactionNumber}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Bill {bill.transactionNumber}
          </Typography>
          <Chip 
            label={bill.status} 
            color={getStatusColor(bill.status) as any}
            sx={{ mr: 1 }}
          />
          {isOverdue(bill.dueDate, bill.balanceDue) && (
            <Chip 
              label="OVERDUE" 
              color="error"
            />
          )}
        </Box>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/accounting/bills')}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          <Button
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            sx={{ mr: 1 }}
          >
            Print
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Bill Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Bill Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Vendor
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bill.vendorName}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Bill Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(bill.transactionDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Due Date
                </Typography>
                <Typography 
                  variant="body1" 
                  gutterBottom
                  color={isOverdue(bill.dueDate, bill.balanceDue) ? 'error' : 'inherit'}
                >
                  {new Date(bill.dueDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  Currency
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {bill.currencyCode}
                </Typography>
              </Grid>
              {bill.apAccountName && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    AP Account
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {bill.apAccountName}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Vendor Address */}
            {bill.vendorAddress && (
              <>
                <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
                  Vendor Address
                </Typography>
                <Typography variant="body2">
                  {bill.vendorAddress.line1}<br />
                  {bill.vendorAddress.line2 && <>{bill.vendorAddress.line2}<br /></>}
                  {bill.vendorAddress.city}, {bill.vendorAddress.state} {bill.vendorAddress.postalCode}<br />
                  {bill.vendorAddress.country}
                </Typography>
              </>
            )}

            {/* Line Items */}
            {bill.lineItems && bill.lineItems.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
                  Line Items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Account</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bill.lineItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>{item.lineNumber}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell>{item.accountName || '-'}</TableCell>
                          <TableCell align="right">{item.quantity || '-'}</TableCell>
                          <TableCell align="right">
                            {item.unitPrice ? formatCurrency(item.unitPrice, bill.currencyCode) : '-'}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.amount, bill.currencyCode)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>

          {/* Related Invoice */}
          {bill.relatedInvoice && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Associated Invoice
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Invoice Number
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {bill.relatedInvoice.invoiceNumber || bill.relatedInvoice.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Customer
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {bill.relatedInvoice.customer || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Due Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {bill.relatedInvoice.dueDate ? new Date(bill.relatedInvoice.dueDate).toLocaleDateString() : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {typeof bill.relatedInvoice.totalAmount === 'number' ? formatCurrency(bill.relatedInvoice.totalAmount, bill.currencyCode) : '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate(`/accounting/invoices/${bill.relatedInvoice!.id}`)}>
                  View Invoice
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Summary Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  Total Amount
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(bill.totalAmount, bill.currencyCode)}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Amount Paid
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(bill.totalAmount - bill.balanceDue, bill.currencyCode)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" color={bill.balanceDue > 0 ? 'error' : 'success.main'}>
                  Balance Due
                </Typography>
                <Typography variant="h6" color={bill.balanceDue > 0 ? 'error' : 'success.main'}>
                  {formatCurrency(bill.balanceDue, bill.currencyCode)}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Transaction Number
                </Typography>
                <Typography variant="body2">
                  {bill.transactionNumber}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(bill.createdTime).toLocaleString()}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Last Updated
                </Typography>
                <Typography variant="body2">
                  {new Date(bill.lastUpdatedTime).toLocaleString()}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};